import { useEffect, useMemo, useRef, useState } from "react";
import { Clock, Check } from "@phosphor-icons/react";

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const ALL_MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

function parseHHmm(value) {
  if (!value) return ["00", "00"];
  if (value instanceof Date) {
    return [
      String(value.getHours()).padStart(2, "0"),
      String(value.getMinutes()).padStart(2, "0"),
    ];
  }
  const s = String(value).trim();
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (m) return [
    String(parseInt(m[1], 10)).padStart(2, "0"),
    String(parseInt(m[2], 10)).padStart(2, "0"),
  ];
  return ["00", "00"];
}

/**
 * WheelPicker — a scrollable column of values (Latin digits only).
 * Emits `onChange(newValue)` when the user picks a row.
 */
function WheelColumn({ items, value, onChange, testId }) {
  const listRef = useRef(null);

  useEffect(() => {
    // Center the currently selected item on open.
    const el = listRef.current;
    if (!el) return;
    const idx = items.indexOf(value);
    if (idx < 0) return;
    const row = el.querySelector(`[data-idx="${idx}"]`);
    if (row) row.scrollIntoView({ block: "center", behavior: "instant" });
  }, [items, value]);

  return (
    <div
      ref={listRef}
      data-testid={testId}
      className="max-h-[220px] overflow-y-auto snap-y snap-mandatory rounded-lg border border-primary/25 bg-[#0B1A3A] px-2 py-2 flex-1"
      style={{ scrollbarWidth: "thin" }}
    >
      {items.map((it, i) => {
        const selected = it === value;
        return (
          <button
            key={it}
            type="button"
            data-idx={i}
            onClick={() => onChange(it)}
            className={`snap-center w-full text-center py-2 my-1 rounded-md font-latin num text-2xl transition-all ${
              selected
                ? "bg-primary text-primary-foreground font-extrabold shadow-[0_0_0_1px_var(--primary)]"
                : "text-white/70 hover:text-primary hover:bg-primary/10"
            }`}
          >
            {it}
          </button>
        );
      })}
    </div>
  );
}

/**
 * ModalTimePicker — app-style 24h picker with hour + minute columns.
 * @param {string} value          "HH:mm"
 * @param {(v:string)=>void} onChange  emits "HH:mm"
 * @param {string} [testId]
 * @param {boolean}[disabled]
 * @param {string} [placeholder]
 */
export default function ModalTimePicker({
  value,
  onChange,
  testId,
  disabled,
  placeholder = "HH:mm",
  minuteStep = 1,
}) {
  const [open, setOpen] = useState(false);
  const [hh, setHh] = useState("00");
  const [mm, setMm] = useState("00");

  // 2026-07 §MinuteStep: filter minutes to multiples of `minuteStep` (1|5|10|15).
  const step = Math.max(1, Math.min(60, Number(minuteStep) || 1));
  const MINUTES = useMemo(
    () => ALL_MINUTES.filter((m) => Number(m) % step === 0),
    [step],
  );

  const snapToStep = (m) => {
    if (step === 1) return m;
    const n = parseInt(m, 10) || 0;
    const snapped = Math.min(59, Math.round(n / step) * step);
    return String(snapped).padStart(2, "0");
  };

  useEffect(() => {
    if (!open) return;
    const [h, m] = parseHHmm(value);
    setHh(h);
    setMm(snapToStep(m));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, value, step]);

  const preview = useMemo(() => `${hh}:${mm}`, [hh, mm]);

  const confirm = () => {
    onChange?.(preview);
    setOpen(false);
  };

  const displayValue = value ? parseHHmm(value).join(":") : "";

  return (
    <>
      <button
        type="button"
        onClick={() => !disabled && setOpen(true)}
        disabled={disabled}
        data-testid={testId}
        className="mz-input flex items-center gap-2 !text-left w-full font-latin num cursor-pointer disabled:opacity-60"
        dir="ltr"
      >
        <Clock size={16} weight="duotone" className="text-primary/70 shrink-0" />
        <span className={displayValue ? "text-white" : "text-white/40"}>
          {displayValue || placeholder}
        </span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          style={{ background: "rgba(5,10,21,0.85)", backdropFilter: "blur(8px)" }}
          onClick={() => setOpen(false)}
          dir="rtl"
          data-testid={testId ? `${testId}-modal` : "time-picker-modal"}
        >
          <div
            className="bg-card border border-primary/30 rounded-2xl w-full max-w-sm gold-glow"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-primary/15 text-center">
              <div className="text-primary font-bold text-lg">الوقت · نظام 24 ساعة</div>
            </div>

            <div className="p-5">
              {/* Columns forced LTR — Hours LEFT, Minutes RIGHT — regardless
                  of surrounding page direction (per app spec). */}
              <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-stretch" dir="ltr">
                <div className="flex flex-col">
                  <div className="text-primary/70 text-xs text-center mb-2 font-bold">الساعة</div>
                  <WheelColumn
                    items={HOURS}
                    value={hh}
                    onChange={setHh}
                    testId={testId ? `${testId}-hh` : "tp-hh"}
                  />
                </div>
                <div className="flex items-center justify-center pt-6">
                  <span className="text-primary text-3xl font-black num">:</span>
                </div>
                <div className="flex flex-col">
                  <div className="text-primary/70 text-xs text-center mb-2 font-bold">الدقيقة</div>
                  <WheelColumn
                    items={MINUTES}
                    value={mm}
                    onChange={setMm}
                    testId={testId ? `${testId}-mm` : "tp-mm"}
                  />
                </div>
              </div>

              {/* Preview */}
              <div className="mt-5 mb-2 text-center">
                <div className="inline-block px-6 py-2 rounded-full border border-primary/40 bg-primary/10">
                  <span
                    className="text-primary font-black num"
                    style={{ fontSize: "28px", letterSpacing: "2px" }}
                    data-testid={testId ? `${testId}-preview` : "tp-preview"}
                  >
                    {preview}
                  </span>
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-primary/15 flex gap-3">
              <button
                type="button"
                onClick={confirm}
                data-testid={testId ? `${testId}-confirm` : "tp-confirm"}
                className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-primary hover:bg-[#E1C484] text-primary-foreground font-bold text-base"
              >
                <Check size={16} weight="bold" /> تأكيد
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-5 py-3 rounded-xl border border-white/20 text-white/70 hover:bg-white/5"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
